import { isBoolean, isNil, isNumber, isObject, isString } from 'lodash';
import { QueryValidator } from 'react-querybuilder';
import { ValidationMap } from 'react-querybuilder';
import { RuleGroupTypeAny } from 'react-querybuilder';
import { RuleType } from 'react-querybuilder';

import {
  QUERY_BUILDER_ARRAY_OPERATORS,
  QUERY_BUILDER_ARRAY_OR_STRING_OPERATORS,
  QUERY_BUILDER_BETWEEN_OPERATORS,
  QUERY_BUILDER_NULL_OPERATORS,
  QueryBuilderOperator,
  QueryBuilderValue,
} from '@dotfile/shared/domain';

type Rule = RuleType<string, QueryBuilderOperator, QueryBuilderValue>;
type RuleGroup = RuleGroupTypeAny<Rule>;

const validateRule = (rule: Rule): { valid: boolean; reasons: string[] } => {
  if (QUERY_BUILDER_BETWEEN_OPERATORS.includes(rule.operator)) {
    let formattedValue = rule.value;

    if (isString(formattedValue)) {
      formattedValue = formattedValue.split(',');
    }

    if (
      !Array.isArray(formattedValue) ||
      formattedValue.filter((value) => !isNil(value)).length !== 2
    ) {
      return {
        valid: false,
        reasons: ['2 values are required for this operator'],
      };
    }
  }

  if (
    QUERY_BUILDER_ARRAY_OPERATORS.includes(rule.operator) ||
    QUERY_BUILDER_ARRAY_OR_STRING_OPERATORS.includes(rule.operator)
  ) {
    let value = rule.value;

    if (isString(value)) {
      value = value.split(',');
    }

    if (!Array.isArray(value) || !value.length) {
      return {
        valid: false,
        reasons: ['Value is required for this operator'],
      };
    }
  }

  if (QUERY_BUILDER_NULL_OPERATORS.includes(rule.operator)) {
    return { valid: true, reasons: [] };
  }

  if (!rule.value && !isNumber(rule.value) && !isBoolean(rule.value)) {
    return {
      valid: false,
      reasons: ['Value is required for this operator'],
    };
  }

  return { valid: true, reasons: [] };
};

const validateRuleGroup = (
  ruleGroup: RuleGroup,
  required = true,
): ValidationMap => {
  const validationMap: ValidationMap = {};

  // Validate empty groups only if required
  if (!ruleGroup.rules || !ruleGroup.rules.length) {
    if (required && ruleGroup.id) {
      validationMap[ruleGroup.id] = {
        valid: false,
        reasons: ['Group cannot be empty'],
      };
    }

    return validationMap;
  }

  // Recursively validate all rules and groups
  ruleGroup.rules.forEach((rule) => {
    if (isObject(rule) && 'rules' in rule) {
      // This is a group
      Object.assign(validationMap, validateRuleGroup(rule, required));
    } else if (isObject(rule) && 'id' in rule && rule.id) {
      const validation = validateRule(rule);

      if (!validation.valid) {
        validationMap[rule.id] = validation;
      }
    }
  });

  return validationMap;
};

// @NOTE This is a validator specific to the query builder component
// We have a similar validator for the jsonLogic format: @dotfile/shared/domain/src/lib/utils/query/json-logic-validator.ts
// Any changes here should be reflected in the json-logic-validator.ts file
export const createRuleGroupValidator = (required = true): QueryValidator => {
  return (ruleGroup: RuleGroupTypeAny): ValidationMap => {
    return validateRuleGroup(ruleGroup as RuleGroup, required);
  };
};
